<script setup>
import Button from "primevue/button";
import { useToast } from "vue-toast-notification";
const toast = useToast();

import PlusIcon from "@/components/shared/icons/PlusIcon.vue";
import OrderApprove from "@/components/shared/icons/OrderApprove.vue";
import CourseApprovalCard from "./CourseApprovalCard.vue";
import { ref, defineEmits, watch, nextTick, toRefs, computed, inject } from "vue";
import SimpleTextDialog from "./SimpleTextDialog.vue";
import { useAddOrUpdateCourseApproval } from "@/components/CurriculumIntegrationApp/composables/useAddOrUpdateCourseApproval.js";
import { useDeleteCourseApproval } from "@/components/CurriculumIntegrationApp/composables/useDeleteCourseApproval.js";
import _ from "lodash";
import { uuid } from "vue-uuid";

const selectedEntityId = inject("selectedEntityId");

// Props and Emits
const props = defineProps({
  modalIsOpen: {
    type: Boolean,
    required: true,
  },
  row: {
    type: Object,
    required: true,
  },
  savedApprovedCourses: {
    type: Array,
    default: () => [],
  },
});

const { savedApprovedCourses } = toRefs(props);

const emit = defineEmits(["closeModal", "allCardsDataBus"]);
const isVisible = ref(props.modalIsOpen);

watch(
  () => props.modalIsOpen,
  (newVal) => {
    isVisible.value = newVal;

    if (newVal) {
      approvedCourses.value = [...savedApprovedCourses.value];
      validationState.value = {};
      validationError.value = false;

      if (approvedCourses.value.length === 0) {
        addNewCourse();
      }
    }
  }
);

const approvedCourses = ref([...savedApprovedCourses.value]);
const deletedCourses = ref([]);

const scrollableContainer = ref(null);
const { mutateAsync: addOrUpdateCourseApproval } = useAddOrUpdateCourseApproval();

const { mutateAsync: deleteCourseApproval } = useDeleteCourseApproval();

const courseCards = ref([]);

const validationState = ref({});
const validationError = ref(false);

const areAllCardsValid = computed(() => {
  if (approvedCourses.value.length === 0) return true;

  return approvedCourses.value.every((course) => !!course.homeClassId);
});

watch(areAllCardsValid, (isValid) => {
  if (isValid) {
    validationError.value = false;
  }
});

const handleValidation = ({ id, isValid }) => {
  validationState.value[id] = isValid;

  if (areAllCardsValid.value) {
    validationError.value = false;
  }
};

watch(
  () => approvedCourses.value.length,
  async () => {
    await nextTick();
    if (scrollableContainer.value) {
      scrollableContainer.value.scrollTo({
        top: scrollableContainer.value.scrollHeight,
        behavior: "smooth",
      });
    }
  }
);

const addNewCourse = () => {
  approvedCourses.value.push({});
};

const itemsWithUUID = computed(() =>
  approvedCourses.value.map((item) => ({
    ...item,
    classId: props?.row?.classId,
    vueId: item.vueId ? item.vueId : uuid.v4(),
    homeEntityId: selectedEntityId.value,
  }))
);

const handleDelete = (deleteId) => {
  const index = itemsWithUUID.value.findIndex((item) => item.vueId === deleteId);
  if (index !== -1) {
    if (itemsWithUUID?.value[index].id) {
      deletedCourses.value = [...deletedCourses.value, itemsWithUUID?.value[index].id];
    }
    approvedCourses.value.splice(index, 1);

    delete validationState.value[deleteId];
  }
};

const closeModal = async () => {
  if (approvedCourses.value) {
    approvedCourses.value = _.cloneDeep(props.savedApprovedCourses);
  }
  validationState.value = {};
  validationError.value = false;

  emit("closeModal", true);
};

const stripEmptyIds = (data) => {
  return data.map((item) => {
    if (item.id === "") {
      const { id, ...rest } = item;
      return rest;
    }
    return item;
  });
};

const saveModal = async () => {
  let isValid = true;

  if (courseCards.value && courseCards.value.length > 0) {
    courseCards.value.forEach((card) => {
      if (card && card.validateFields) {
        const cardValid = card.validateFields();
        isValid = isValid && cardValid;
      }
    });
  }

  const hasEmptyRequiredFields = approvedCourses.value.some((card) => !card.homeClassId);

  if (hasEmptyRequiredFields && approvedCourses.value.length > 0) {
    validationError.value = true;
    return;
  }

  validationError.value = false;

  let updatedItems = itemsWithUUID.value.filter((item) => item.homeClassId != "");
  updatedItems = stripEmptyIds(updatedItems);

  try {
    closeModal();
    const approvalAddOrUpdateResponse = await addOrUpdateCourseApproval(updatedItems);

    approvedCourses.value = approvalAddOrUpdateResponse;

    if (deletedCourses.value.length > 0) {
      await deleteCourseApproval(deletedCourses.value);
    }

    deletedCourses.value = [];
    toast.success("Course approval data saved successfully");
    emit("allCardsDataBus", approvedCourses.value);
  } catch (error) {
    toast.error("Failed to save changes, please try again later");
  }
};

const updateCourseData = (courseObject) => {
  let index;
  index = itemsWithUUID.value.findIndex((item) => item.vueId === courseObject.value.vueId);
  approvedCourses.value[index] = courseObject.value;

  if (areAllCardsValid.value) {
    validationError.value = false;
  }
};
</script>

<template>
  <SimpleTextDialog v-model="isVisible" :modal="true" :closable="true" @close="closeModal">
    <template #header>
      <div class="flex items-center justify-between w-full bg-teal-light">
        <div class="flex items-center">
          <OrderApprove />
          <span class="font-semibold pl-1 text-university-primary">APPROVE</span>
        </div>
      </div>
    </template>

    <div ref="scrollableContainer" class="w-full px-6 pt-6 overflow-y-auto" style="height: calc(75vh - 130px);">
      <div class="pb-6 px-6">
        <span>Approving for </span>
        <span class="font-semibold">{{ row.classTitleAndHomeInstitution }}&nbsp;</span>
      </div>

      <CourseApprovalCard
        v-for="(row, index) in itemsWithUUID"
        :key="row.vueId"
        ref="courseCards"
        :host-class-id="props.row.classId"
        :row="row"
        :index="index"
        @individual-card-data-bus="updateCourseData"
        @validation="handleValidation"
        @delete="handleDelete"
      />

      <div v-if="validationError" class="text-red-500 text-sm mt-4 px-6">
        Please fill in all required fields before saving.
      </div>
    </div>

    <template #footer>
      <div class="w-full flex justify-between items-center px-6 pt-3 border-t border-gray-300">
        <button
          @click="addNewCourse"
          class="flex items-center text-university-secondary hover:text-teal-300"
          aria-label="Add Another"
        >
          <PlusIcon />
          <span class="text-university-primary pl-2">Add Another</span>
        </button>

        <div class="flex space-x-4">
          <button
            @click="closeModal"
            class="bg-white text-university-secondary border border-university-secondary font-semibold px-6 py-3"
          >
            CANCEL
          </button>
          <Button @click="saveModal" label="SAVE" class="bg-university-secondary font-semibold text-white px-6 py-3" />
        </div>
      </div>
    </template>
  </SimpleTextDialog>
</template>
